// ── Centralized profile cache ─────────────────────────────────────────────────
// The `profiles` mirror of useFollows' cache, for the same reason at bigger scale:
// 25 call sites independently run `from('profiles').select(...).in('id', ids)` to
// hydrate the SAME few ids — the live table is ~40 rows and has taken 1.6M index
// scans, and a single Home mount fired ~10 of these (bell ×2, inbox ×3, marquee ×2,
// tonight ×2…). One fetch per id per session now; concurrent callers share the
// in-flight promise (consolidation audit 2026-08-01).
//
// A plain async fn, not a hook, because most call sites are inside loaders and
// realtime handlers where a hook can't go. Column set is the union every site
// needs — id, display_name, username, avatar_url — and stays inside the
// column-security allow-list (RLS: profiles are column-secured; these four are the
// public set every existing site already reads).
//
// After a profile edit, call invalidateProfiles(ids?) — same contract as
// invalidateFollows.
import { supabase } from './supabase'

const cache = new Map()      // id → profile row (or null for a miss, so we never re-fetch a deleted user)
const pending = new Map()    // id → Promise resolving that id's row

export async function fetchProfiles(ids) {
  const want = [...new Set((ids || []).filter(Boolean))]
  if (!want.length) return {}

  const missing = want.filter(id => !cache.has(id) && !pending.has(id))
  if (missing.length) {
    const p = supabase.from('profiles')
      .select('id, display_name, username, avatar_url')
      .in('id', missing)
      .then(({ data }) => {
        const got = new Set()
        for (const row of data || []) { cache.set(row.id, row); got.add(row.id) }
        // Cache misses as null — an id that returned nothing (deleted user, RLS)
        // must not be re-queried by every future caller.
        for (const id of missing) { if (!got.has(id)) cache.set(id, null); pending.delete(id) }
      })
      .catch(() => { for (const id of missing) pending.delete(id) })
    for (const id of missing) pending.set(id, p)
  }

  // Wait for whatever's in flight for our ids (ours or another caller's).
  await Promise.all(want.map(id => pending.get(id)).filter(Boolean))

  const out = {}
  for (const id of want) { const row = cache.get(id); if (row) out[id] = row }
  return out   // { id → {id, display_name, username, avatar_url} } — misses omitted
}

export function invalidateProfiles(ids) {
  if (!ids) { cache.clear(); pending.clear(); return }
  for (const id of Array.isArray(ids) ? ids : [ids]) { cache.delete(id); pending.delete(id) }
}
